import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { AppProvider, useApp } from "../context/AppContext";
import AIPanel from "./AIPanel";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Sheet, SheetContent, SheetTitle, SheetDescription } from "./ui/sheet";
import { Button } from "./ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel } from "./ui/dropdown-menu";
import {
  LayoutDashboard, ClipboardList, GitCompareArrows, Store, Boxes, Wallet, Users,
  Sparkles, Bell, LogOut, Search, Building2, Menu, BrainCircuit, Target,
  CalendarDays, ListTodo, Package, Database, FileBarChart,
} from "lucide-react";
import { useState } from "react";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/daily", label: "Daily Work", icon: CalendarDays },
  { to: "/requirements", label: "Requirements", icon: ListTodo },
  { to: "/stock", label: "Stock", icon: Package },
  { to: "/reports", label: "Reports", icon: FileBarChart },
  { to: "/analytics", label: "AI Analytics", icon: BrainCircuit },
  { to: "/goals", label: "Goals", icon: Target },
  { to: "/procurement", label: "Procurement Log", icon: ClipboardList },
  { to: "/quotations", label: "Vendor Quotes", icon: GitCompareArrows },
  { to: "/vendors", label: "Vendor Directory", icon: Store },
  { to: "/inventory", label: "Inventory & Audit", icon: Boxes },
  { to: "/budget", label: "Budget Tracker", icon: Wallet },
  { to: "/masters", label: "Master Data", icon: Database },
  { to: "/team", label: "Team & Roles", icon: Users },
];

const ROLE_LABEL = {
  admin: "Super Admin", procurement: "Procurement Lead",
  requisitioner: "Branch Manager", finance: "Finance",
};

function NavItems({ onNavigate }) {
  return (
    <>
      {NAV.map((n) => (
        <NavLink
          key={n.to} to={n.to} onClick={onNavigate}
          data-testid={`nav-${n.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
          className={({ isActive }) =>
            `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors border-l-2 ${
              isActive ? "bg-white/10 text-white border-brand-emerald" : "text-slate-300 border-transparent hover:bg-white/5 hover:text-white"
            }`
          }>
          <n.icon className="w-[18px] h-[18px]" /> {n.label}
        </NavLink>
      ))}
    </>
  );
}

function Shell() {
  const { user, logout } = useAuth();
  const { meta, branch, setBranch, openAI } = useApp();
  const [q, setQ] = useState("");
  const [mobileNav, setMobileNav] = useState(false);

  return (
    <div className="min-h-screen flex bg-background">
      {/* Sidebar */}
      <aside className="w-64 bg-navy text-slate-100 flex-col hidden md:flex fixed inset-y-0 left-0 z-30">
        <div className="h-16 flex items-center gap-3 px-5 border-b border-white/10">
          <div className="w-9 h-9 rounded-lg bg-brand-teal flex items-center justify-center">
            <Sparkles className="w-5 h-5 text-white" />
          </div>
          <div>
            <div className="font-heading font-bold leading-tight text-sm">Youngness Centre</div>
            <div className="text-[10px] tracking-widest uppercase text-slate-400">Procurement CRM</div>
          </div>
        </div>
        <nav className="flex-1 py-4 px-3 space-y-1 overflow-y-auto">
          <NavItems />
        </nav>
        <div className="p-3 border-t border-white/10">
          <button
            data-testid="sidebar-ai-button"
            onClick={() => openAI("")}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg bg-brand-teal/20 text-teal-100 hover:bg-brand-teal/30 transition-colors text-sm font-medium">
            <Sparkles className="w-[18px] h-[18px]" /> AI Assistant
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 md:ml-64 flex flex-col min-h-screen min-w-0 overflow-x-hidden">
        {/* Header */}
        <header className="h-16 bg-white border-b border-border flex items-center gap-3 px-4 md:px-6 sticky top-0 z-20">
          <button data-testid="mobile-menu-button" onClick={() => setMobileNav(true)}
            className="md:hidden w-9 h-9 rounded-lg hover:bg-slate-100 flex items-center justify-center text-navy shrink-0">
            <Menu className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-2 text-brand-slate shrink-0">
            <Building2 className="w-4 h-4" />
            <Select value={branch} onValueChange={setBranch}>
              <SelectTrigger data-testid="branch-selector" className="w-[130px] md:w-[160px] h-9 border-border">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="All">All Branches</SelectItem>
                {meta.branches.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <form
            className="flex-1 max-w-xl hidden sm:block"
            onSubmit={(e) => { e.preventDefault(); if (q.trim()) openAI(q.trim()); }}>
            <div className="relative">
              <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                data-testid="ai-search-bar"
                value={q} onChange={(e) => setQ(e.target.value)}
                placeholder="Ask AI: 'Which branch spent most on Cosmeceuticals?'"
                className="w-full h-9 pl-9 pr-3 rounded-full bg-slate-50 border border-border text-sm focus:outline-none focus:ring-2 focus:ring-brand-teal/40" />
            </div>
          </form>

          <div className="ml-auto flex items-center gap-2">
            <Button data-testid="header-ai-button" onClick={() => openAI("")} variant="outline"
              className="h-9 gap-2 border-brand-teal text-brand-teal hover:bg-teal-50">
              <Sparkles className="w-4 h-4" /> <span className="hidden lg:inline">AI Panel</span>
            </Button>
            <button data-testid="notification-bell" className="relative w-9 h-9 rounded-lg hover:bg-slate-100 flex items-center justify-center text-brand-slate">
              <Bell className="w-5 h-5" />
              <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-rose-500" />
            </button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="user-menu" className="flex items-center gap-2 pl-1 pr-2 h-9 rounded-lg hover:bg-slate-100">
                  <img src={user?.picture || "https://images.unsplash.com/photo-1622253692010-333f2da6031d?w=80&q=80"} alt="avatar" className="w-7 h-7 rounded-full object-cover" />
                  <div className="text-left hidden md:block">
                    <div className="text-xs font-semibold text-navy leading-tight">{user?.name}</div>
                    <div className="text-[10px] text-brand-slate">{ROLE_LABEL[user?.role] || user?.role}</div>
                  </div>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="text-sm">{user?.name}</div>
                  <div className="text-xs text-brand-slate font-normal">{user?.email}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="logout-button" onClick={logout} className="text-rose-600">
                  <LogOut className="w-4 h-4 mr-2" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <main className="flex-1 p-4 md:p-8 min-w-0 overflow-x-hidden">
          <Outlet />
        </main>
      </div>

      <AIPanel />

      <Sheet open={mobileNav} onOpenChange={setMobileNav}>
        <SheetContent side="left" style={{ backgroundColor: "#0F172A" }} className="w-64 p-0 border-0 text-slate-100">
          <SheetTitle className="sr-only">Navigation menu</SheetTitle>
          <SheetDescription className="sr-only">Links to all procurement modules</SheetDescription>
          <div className="h-16 flex items-center gap-3 px-5 border-b border-white/10">
            <div className="w-9 h-9 rounded-lg bg-brand-teal flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <div className="text-white">
              <div className="font-heading font-bold leading-tight text-sm">Youngness Centre</div>
              <div className="text-[10px] tracking-widest uppercase text-slate-400">Procurement CRM</div>
            </div>
          </div>
          <nav className="py-4 px-3 space-y-1">
            <NavItems onNavigate={() => setMobileNav(false)} />
            <button onClick={() => { setMobileNav(false); openAI(""); }}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg bg-brand-teal/20 text-teal-100 hover:bg-brand-teal/30 text-sm font-medium mt-2">
              <Sparkles className="w-[18px] h-[18px]" /> AI Assistant
            </button>
          </nav>
        </SheetContent>
      </Sheet>
    </div>
  );
}

export default function Layout() {
  return (
    <AppProvider>
      <Shell />
    </AppProvider>
  );
}
